/* ===================================================================
   CONFIGURATOR FLOORPLAN DATA
   assets/configurator-floorplan-data.js

   Pure data file — all available floorplans live here. Each entry
   contains:
     • Metadata for filtering (bedrooms, storeys, category, area)
     • Localized names (cs/en)
     • Footprint polygon (= the foundations outline, in meters)
     • Room layout (= geometry + classification)
     • Optional walls (exterior + interior)
     • Optional doors / windows

   Coordinate system convention:
     • Origin (0, 0) = top-left of the floorplan, looking at it in 2D
     • x axis = length (horizontal), grows to the right
     • y axis = depth (vertical), grows downward
     • All distances in METERS
     • Rooms are axis-aligned rectangles: { x, y, w, h }
       (more complex shapes can be expressed as a polygon path later)

   The renderer reads these objects and:
     • Builds the drawer card preview
     • Renders a 2D SVG overlay on the configurator floor
     • Extrudes rooms + walls into Three.js geometry for the 3D view
     • Updates the foundations polygon to match the footprint

   Add new floorplans by pushing additional entries onto
   window.FloorplanData.floorplans. Order in the array drives drawer
   display order (newest first is fine).
   =================================================================== */

(function (global) {
  'use strict';

  /* Stable room type → palette. The renderer pulls colours from
     here, so adding a new room type means adding a colour here
     and the rest of the pipeline picks it up automatically. */
  var ROOM_PALETTE = {
    living:   '#e74c3c',  /* red          */
    kitchen:  '#f39c12',  /* orange       */
    dining:   '#d35d00',  /* dark orange  */
    bedroom:  '#3498db',  /* blue         */
    kids:     '#2ecc71',  /* green        */
    bath:     '#1abc9c',  /* teal         */
    toilet:   '#16a085',  /* dark teal    */
    hall:     '#bdc3c7',  /* gray         */
    storage:  '#95a5a6',  /* darker gray  */
    utility:  '#8e44ad',  /* purple       */
    office:   '#9b59b6',  /* light purple */
    workshop: '#34495e',  /* slate        */
    terrace:  '#f1c40f',  /* yellow       */
    other:    '#7f8c8d'   /* fallback     */
  };

  /* ─────────────────────────────────────────────────────────────────
     FLOORPLAN: Four-Bedroom Barndominium
     Single-storey rural house, 9.2 × 20.5 m. Generous open
     kitchen-dining-living core on the left, four bedrooms with
     ensuite / shared baths on the right, plus a workshop and mud
     room. Front porch runs the full depth on the west side.
     ───────────────────────────────────────────────────────────────── */
  var FP_BARNDO_4BR = {
    handle:        'barndo-4br-9x20',
    category:      'house',
    storeys:       1,
    bedrooms:      4,
    area_m2:       165.6,   /* indoor area excl porch */
    porch_m2:      23.0,
    footprint_m:   { length: 20.5, depth: 9.2 },

    /* External wall + internal partition thickness (informational —
       walls drawn in SVG already include their thickness in their
       geometry; this is here so a later structure stage can read it
       without re-parsing the SVG). */
    walls: {
      exterior_thickness_m: 0.33,
      interior_thickness_m: 0.15
    },

    /* The polygon that drives the foundations. Counter-clockwise,
       starting top-left. Includes the porch — the foundations have
       to extend under it. */
    footprint_polygon: [
      [0.0,  0.0],
      [20.5, 0.0],
      [20.5, 9.2],
      [0.0,  9.2]
    ],

    /* Where the porch ends and the heated envelope begins. The
       renderer can use this to colour the porch differently or to
       allow the user to drop the porch entirely. */
    porch_x_end_m: 2.5,

    /* Room rectangles. type → palette colour. label is human-
       readable and gets drawn on top of the room in the 2D overlay
       (in addition to the area).  All coordinates in meters,
       origin at top-left. */
    rooms: [
      { id: 'porch',          type: 'terrace',  label_cs: 'Veranda',         label_en: 'Porch',          x:  0.0, y: 0.0, w: 2.5, h: 9.2 },
      { id: 'kitchen',        type: 'kitchen',  label_cs: 'Kuchyně',         label_en: 'Kitchen',        x:  2.5, y: 0.0, w: 3.5, h: 3.0 },
      { id: 'open_living',    type: 'living',   label_cs: 'Jídelna / Obývák', label_en: 'Dining / Living', x:  2.5, y: 3.0, w: 6.5, h: 6.2 },
      { id: 'open_dining',    type: 'dining',   label_cs: '',                label_en: '',               x:  6.0, y: 0.0, w: 3.0, h: 3.0 },
      { id: 'bedroom_1',      type: 'bedroom',  label_cs: 'Ložnice #1',      label_en: 'Bedroom #1',     x:  9.0, y: 0.0, w: 4.0, h: 4.0 },
      { id: 'bath_1',         type: 'bath',     label_cs: 'Koupelna',        label_en: 'Bathroom',       x:  9.0, y: 4.0, w: 2.0, h: 1.5 },
      { id: 'hall_1',         type: 'hall',     label_cs: 'Chodba',          label_en: 'Hall',           x: 11.0, y: 4.0, w: 2.0, h: 1.5 },
      { id: 'bedroom_3',      type: 'bedroom',  label_cs: 'Ložnice #3',      label_en: 'Bedroom #3',     x:  9.0, y: 5.5, w: 4.0, h: 3.7 },
      { id: 'bedroom_2',      type: 'bedroom',  label_cs: 'Ložnice #2',      label_en: 'Bedroom #2',     x: 13.0, y: 0.0, w: 4.0, h: 4.0 },
      { id: 'bath_2',         type: 'bath',     label_cs: 'Koupelna',        label_en: 'Bathroom',       x: 13.0, y: 4.0, w: 2.0, h: 1.5 },
      { id: 'storage',        type: 'storage',  label_cs: 'Sklad',           label_en: 'Storage',        x: 15.0, y: 4.0, w: 2.0, h: 1.5 },
      { id: 'master_bedroom', type: 'bedroom',  label_cs: 'Hlavní ložnice',  label_en: 'Master Bedroom', x: 13.0, y: 5.5, w: 4.0, h: 3.7 },
      { id: 'shop',           type: 'workshop', label_cs: 'Dílna',           label_en: 'Workshop',       x: 17.0, y: 0.0, w: 3.5, h: 4.5 },
      { id: 'mud',            type: 'utility',  label_cs: 'Šatna',           label_en: 'Mud Room',       x: 17.0, y: 4.5, w: 3.5, h: 1.5 },
      { id: 'master_bath',    type: 'bath',     label_cs: 'Hlavní koupelna', label_en: 'Master Bath',    x: 17.0, y: 6.0, w: 3.5, h: 3.2 }
    ],

    /* Doors and windows — minimal seed data, expanded later.
       For now only used by the 2D overlay to draw gaps in walls. */
    doors:   [],
    windows: []
  };

  global.FloorplanData = {
    VERSION: 'floorplan-data-v1',

    /* Palette is exposed so the lib + 3D renderer can pick colours
       up consistently without duplicating the table. */
    palette: ROOM_PALETTE,

    /* Single source of truth — the renderer pulls floorplans from
       here.  Newest first; the drawer respects this order. */
    floorplans: [
      FP_BARNDO_4BR
    ]
  };

})(window);